import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@repo/shared";
import { useHistory } from "react-router-dom";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import BootstrapTable from 'react-bootstrap-table-next';
import filterFactory, { textFilter, selectFilter, Comparator } from 'react-bootstrap-table2-filter';
import NoData from '../common/NoData';
import SchoolDataService from "../../services/school/SchoolService";
import ClassDataService from "../../services/class/ClassService";
import StudentDataService from "../../services/student/StudentService";
import LoadingSpinner from '../common/LoadingSpinner';
import { ButtonMailto } from '../common/Buttons';
import { useAcademicYear } from "../../contexts/AcademicYearContext";

import 'react-bootstrap-table2-filter/dist/react-bootstrap-table2-filter.min.css';
import SortCaret from '../common/SortCaret';


const SchoolsList = () => {
  const history = useHistory();
  const { appTheme } = useTheme();
  const { activeYear, activeSemester } = useAcademicYear();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredRowCount, setFilteredRowCount] = useState(0);
  const schoolsRef = useRef();
  const activeYearRef = useRef(null);

  const retrieveSchools = async() => {
    try {
      const response = await SchoolDataService.getAll();
      console.log(response);
      //transform null kg to 0 for filtering purposes
      response.forEach(school => school.kindergarten = school.kindergarten ? 1 : 0);
      setSchools(response);
      setFilteredRowCount(response.length);
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  const getBodyForSchoolDirektorEmail = async (schoolId, isKindergarten) => {
    const classesForSchool = await ClassDataService.getAllForSchool(schoolId);

    if (classesForSchool) {
      const populatedClasses = await Promise.all(classesForSchool.filter(c => c.schoolYear === activeYearRef.current?.year).map(async c => {
        const studentsForClass = await StudentDataService.getAllForClass(c.id);
        c.students = studentsForClass;
        return c;
      }));

      const bodyString = populatedClasses.sort(ClassDataService.classChronoSortFunc).map(c => {
        const classString = `Class: ${c.title} on ${c.day} at ${c.time} for year ${c.schoolYear}`;
        const studentsString = c.students
          .map(s => {
            if (isKindergarten) {
              return `  ${s.firstName} ${s.lastName} (${s.age} - ${s.className})`
            } else {
              return `  ${s.firstName} ${s.lastName} (${s.className})`
            }
          })
          .join('\n');
        return `${classString}\n${studentsString}`;
      }).join('\n\n');

      console.log('Generated email body for school:', bodyString);
      return bodyString;
    }

    return '';
  }

  const getEmailAddressesForSchoolParents = async (classId) => {
    console.log('Getting emailAddresses for class', classId);

    const studentsForClass = await StudentDataService.getAllStudentsForSchoolAndYear(classId, activeYearRef.current?.year);

    return studentsForClass.map(s => s.emailAddress);
  }

  const getBodyForSchoolParentsEmail = async (schoolId) => {

    const emailBody = await SchoolDataService.getEmailForParents(schoolId);

    return emailBody;
  }

  const sendDirektorEmail = async (schoolId, emailAddress, isKindergarten) => {
    console.log('Active Year:', activeYear, 'Active Semester:', activeSemester);
    const body = await getBodyForSchoolDirektorEmail(schoolId, isKindergarten);
    if (emailAddress) {
      window.location.href = `mailto:${emailAddress}?subject=Message from ${appTheme?.appName || ''}&body=${encodeURIComponent(body)}`;
    }
  };

  useEffect(() => {
    document.title = `${appTheme?.appName || ''} - Schools`;
    retrieveSchools();
  }, [appTheme]);

  useEffect(() => {
    schoolsRef.current = schools;
  }, [schools]);

  useEffect(() => {
    activeYearRef.current = activeYear;
  }, [activeYear]);

  const addSchool = () => {
    history.push("school/");
  }
  const openSchool = (row) => {
    history.push("school/" + row.original.id);
  };

  const schoolColumns = [
    { sort: true, dataField: 'name', text: 'Name', filter: textFilter(), sortCaret: SortCaret },
    { dataField: 'address', text: 'Address', filter: textFilter() },
    { dataField: 'phoneNumber', text: 'Phone Number', filter: textFilter() },
    {
      dataField: 'emailAddress', text: 'Email', filter: textFilter(),
      formatter: (cell, row) => (
        <a style={{ color: 'blue' }} onClick={(e) => {
          e.stopPropagation();
          console.log('School email clicked for', row.id);
          sendDirektorEmail(row.id, row.emailAddress, row.kindergarten === 1);
        }}>
          {cell}
        </a>
      )
    },
    {
      sort: true, dataField: 'kindergarten', text: 'Kindergarten', filter: selectFilter({ options: { 1: 'Yes', 0: 'No' }, comparator: Comparator.LIKE }), sortCaret: SortCaret,
      formatter: (cell, _row) => (
        cell === 1 ? 'Yes' : 'No'
      )
    },
    {
      dataField: 'actions',
      text: 'Email Parents',
      editable: false,
      formatter: (cell, row) => (
        <ButtonMailto
          sent={!!row.emailSentList}
          emailAddressesCallback={() => getEmailAddressesForSchoolParents(row.id)}
          subject={`Message from ${appTheme?.appName || ''}`}
          bodyCallback={() => getBodyForSchoolParentsEmail(row.id, row.semester)} />
      ),
      headerStyle: { width: '80px', textAlign: 'center' },
      align: 'center',
    }
  ];

  const tableNoDataIndication = () => {
    if (loading) return <div className="p-4"><LoadingSpinner /></div>;
    return <NoData type="schools" />;
  };

  function afterFilter(newResult, _newFilters) {
    setFilteredRowCount(newResult.length);
  }

  return (
    <Container className="py-2 py-lg-4">
      <Row className="justify-content-center">
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2">
                <h2 className="mb-0">Schools</h2>
                <Button variant="primary" size="sm" onClick={addSchool} className="ms-auto">
                  <FontAwesomeIcon icon={faPlus} className="me-2" /> Add School
                </Button>
              </div>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Showing <strong>{filteredRowCount}</strong> of <strong>{schools.length}</strong> schools.
              </div>
            </Card.Header>
            <Card.Body className="p-0" style={{ overflowX: 'auto' }}>
              <BootstrapTable
                keyField='id'
                striped
                hover
                condensed
                data={schools}
                columns={schoolColumns}
                bordered={false}
                classes="mb-0"
                noDataIndication={tableNoDataIndication}
                filter={filterFactory({ afterFilter })}
                rowEvents={{ onClick: (e, row) => openSchool({ original: row }) }}
              />
            </Card.Body>
            <Card.Footer className="text-muted">
              Showing <strong>{filteredRowCount}</strong> of <strong>{schools.length}</strong> schools.
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SchoolsList;